import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { Button } from "../components/ui/button";
import { ArrowRight, ShieldCheck, Award, Microscope, PackageCheck } from "lucide-react";

const qcIcons = [PackageCheck, Microscope, ShieldCheck];

const Factory = () => {
  const { content } = useContent();
  const f = content.factoryPage;
  const stats = content.stats;

  return (
    <div>
      <section className="relative h-[46vh] min-h-[360px] overflow-hidden bg-[#14171c]">
        <img src={f.heroImage} alt="Factory" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e1116]/90 to-[#0e1116]/40" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <span className="text-[#E7C766] font-bold text-sm uppercase tracking-widest">{f.heroEyebrow}</span>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl text-white mt-3 max-w-2xl">{f.heroTitle}</h1>
            <p className="text-neutral-300 mt-4 max-w-2xl">{f.heroSubtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-display font-extrabold text-4xl md:text-5xl text-gold-gradient">{s.value}</div>
              <div className="text-neutral-500 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[#C9A227] font-bold text-sm uppercase tracking-widest">{f.aboutEyebrow}</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3 mb-5">{f.aboutTitle}</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">{f.aboutText1}</p>
            <p className="text-neutral-600 leading-relaxed mb-6">{f.aboutText2}</p>
            <Link to="/contact">
              <Button className="bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-full h-12 px-7">
                Start Your Project <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {(f.aboutImages || []).slice(0, 4).map((img, i) => (
              <img key={i} src={img} alt="Factory" className="rounded-2xl h-56 w-full object-cover" />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-bold text-sm uppercase tracking-widest">Production Process</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3">From Artwork to Worldwide Shipping</h2>
            <div className="gold-rule mx-auto mt-5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(f.process || []).map((p, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-neutral-100 card-lift">
                <div className="font-display font-extrabold text-4xl text-[#C9A227]/25 mb-3">{p.step}</div>
                <h3 className="font-display font-bold text-lg text-[#14171c] mb-2">{p.title}</h3>
                <p className="text-neutral-600 text-[15px] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-bold text-sm uppercase tracking-widest">Quality Control</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3">Inspected at Every Stage</h2>
            <div className="gold-rule mx-auto mt-5" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {(f.qualityControl || []).map((q, i) => {
              const Icon = qcIcons[i % qcIcons.length];
              return (
                <div key={i} className="p-8 rounded-2xl border border-neutral-100 text-center card-lift">
                  <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-5">
                    <Icon className="w-7 h-7 text-[#C9A227]" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-[#14171c] mb-2">{q.title}</h3>
                  <p className="text-neutral-600 text-[15px]">{q.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-14 text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Award className="w-5 h-5 text-[#C9A227]" />
              <span className="font-display font-bold text-[#14171c]">Certifications</span>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {(f.certifications || []).map((c) => (
                <span key={c} className="px-5 py-2 rounded-full bg-neutral-100 text-neutral-700 font-semibold text-sm">{c}</span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Factory;
